import { BitkitButton, BitkitTextInput } from '@bitrise/bitkit-v2';
import { Box } from '@chakra-ui/react/box';
import { chakra } from '@chakra-ui/react/styled-system';
import { datadogRum } from '@datadog/browser-rum';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { ValidatedRevealablePasswordInput } from '@/components/RevealablePasswordInput/RevealablePasswordInput';
import useAlert from '@/hooks/useAlert';
import { SignUpRequest } from '@/models/Auth';
import { getDisplayableError, getMultipleDisplayableErrors } from '@/utils/formValidators/commonErrors';
import { emailValidationRule, usernameValidationRule } from '@/utils/formValidators/rules';
import isRunningInSelenium from '@/utils/isRunningInSelenium';
import { navigateToUrl } from '@/utils/navigation';

import TermsOfServiceAndPrivacyPolicy from '../../components/TermsOfServiceAndPrivacyPolicy/TermsOfServiceAndPrivacyPolicy';
import useSignUp from '../../hooks/useSignUp/useSignUp';
import { useValidateUsername } from '../../hooks/useValidateUsername/useValidateUsername';

type SignUpFormProps = {
  prefillEmail?: string;
  forcePrefilled?: boolean;
};

type FormValues = Partial<SignUpRequest['user']>;

const SignUpForm = ({ prefillEmail, forcePrefilled }: SignUpFormProps) => {
  const executedByBot = isRunningInSelenium();
  const { removeAlert } = useAlert();
  const [passwordRequirementsMet, setPasswordRequirementsMet] = useState<boolean>(false);
  const { mutate: submitSignUp, isPending, isSuccess, fieldErrors, fieldErrorsAll } = useSignUp();

  const [isServerErrorHiddenFor, setIsServerErrorHiddenFor] = useState({
    email: false,
    username: false,
    password: false,
  });

  const {
    watch,
    control,
    register,
    setValue,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({ mode: 'onChange' });

  const email = watch('email');
  const username = watch('username');
  const consentAccepted = watch('isSendNewsletter', false);

  const { validationError: usernameAsyncError } = useValidateUsername(username || '');

  const resetHideServerError = () => {
    setIsServerErrorHiddenFor({
      email: false,
      username: false,
      password: false,
    });
  };

  const hideServerErrorOf = (field: keyof typeof isServerErrorHiddenFor) => () => {
    setIsServerErrorHiddenFor((prev) => ({ ...prev, [field]: true }));
  };

  const emailProps = register('email', {
    ...emailValidationRule,
    onChange: hideServerErrorOf('email'),
  });

  const usernameProps = register('username', {
    ...usernameValidationRule,
    onChange: hideServerErrorOf('username'),
  });

  const passwordProps = register('password', {
    required: 'Password is required.',
    onChange: hideServerErrorOf('password'),
  });

  useEffect(() => {
    if (prefillEmail) {
      setValue('email', prefillEmail);
    }
  }, [prefillEmail, setValue]);

  const emailErrorTextClient = getDisplayableError(errors?.email?.message, 'Email');
  const emailErrorTextServer = getDisplayableError(fieldErrors?.email?.error, 'Email');
  const emailErrorText = isServerErrorHiddenFor.email ? emailErrorTextClient : emailErrorTextServer;

  const usernameErrorTextClient = getDisplayableError(usernameAsyncError || errors?.username?.message, 'Username');
  const usernameErrorTextServer = getDisplayableError(fieldErrors?.username?.error, 'Username');
  const usernameErrorText = isServerErrorHiddenFor.username ? usernameErrorTextClient : usernameErrorTextServer;

  const passwordErrorTextClient = getDisplayableError(errors?.password?.message, 'Password');
  const passwordErrorTextServer = getMultipleDisplayableErrors(fieldErrorsAll?.password, 'Password');
  const passwordErrorText = isServerErrorHiddenFor.password ? passwordErrorTextClient : passwordErrorTextServer;

  const hasFieldError = !!emailErrorText || !!usernameErrorText || !!passwordErrorText || !consentAccepted;
  const isSubmitDisabled = hasFieldError || isPending || isSuccess || executedByBot || !passwordRequirementsMet;

  const onSubmit = handleSubmit((values) => {
    if (executedByBot) {
      return;
    }

    removeAlert();
    datadogRum.addAction('submit');

    submitSignUp(
      {
        user: {
          email,
          username,
          password: values.password,
          isSendNewsletter: !!values.isSendNewsletter,
        },
      } as SignUpRequest,
      {
        onSuccess: ({ headers: { location } }) => {
          navigateToUrl(location);
        },
        onError: resetHideServerError,
      },
    );
  });

  return (
    <chakra.form onSubmit={onSubmit} display="flex" flexDirection="column" gap="16" noValidate>
      <BitkitTextInput
        placeholder="Email"
        label="Email"
        errorText={emailErrorText}
        state={forcePrefilled ? 'disabled' : undefined}
        inputProps={{ ...emailProps, autoFocus: true, type: 'email' }}
      />
      <BitkitTextInput
        placeholder="Username"
        label="Username"
        errorText={usernameErrorText}
        inputProps={usernameProps}
      />
      <ValidatedRevealablePasswordInput
        email={email}
        username={username}
        label="Password"
        placeholder="Password"
        errorText={passwordErrorText}
        onValidate={setPasswordRequirementsMet}
        inputProps={passwordProps}
      />
      <Box display="flex" flexDirection="column" gap="16">
        <Controller
          control={control}
          name="isSendNewsletter"
          rules={{ required: 'Agreeing to Terms of Service and Privacy Policy is required' }}
          render={({ field }) => (
            <TermsOfServiceAndPrivacyPolicy
              checked={!!field.value}
              name={field.name}
              onCheckedChange={(details) => field.onChange(details.checked === true)}
            />
          )}
        />
        <BitkitButton
          state={isPending ? 'loading' : isSubmitDisabled ? 'disabled' : undefined}
          width="100%"
          type="submit"
        >
          Sign up
        </BitkitButton>
      </Box>
    </chakra.form>
  );
};

export default SignUpForm;
